/**
 * Persistent FIFO queue for worker (field) mutations — §3.9.
 *
 * Зачем. Рабочий заполняет смену на цеховом планшете с нестабильным Wi-Fi.
 * Если submit падает по сети — теряем данные смены. Решение: каждая mutation
 * (POST/PUT/DELETE `/field/timesheet/entries`, POST `/field/timesheet/sign`)
 * пишется в localStorage; при network-error она остаётся в очереди и
 * автоматически дренируется на `online`-событии.
 *
 * Решения assessment'а §3.9 заложены здесь:
 *   - CSRF cookie может expire'нуть пока заявка лежит в очереди. На 403 от
 *     CSRF middleware (detail.code === "csrf_invalid" | "csrf_missing") runner
 *     синхронно зовёт GET /api/me чтобы рефрешнуть cookie, и повторяет один
 *     раз. Дальше — терминальный fail.
 *   - Ключ очереди привязан к user.id (`field.queue.<id>`) и чистится в
 *     FieldShell.logout — иначе после смены пользователя в одном PWA
 *     прилетит чужой payload под куки нового рабочего.
 *   - Sign-after-entries: перед отправкой подписи UI ждёт `drainPending`,
 *     чтобы backend увидел свежий `sum_entries`. См. FieldSignPage.
 *
 * Очередь — `entry-*` мутации **строго последовательно** (порядок важен,
 * например create→update→delete на одну запись). Параллелизм бесполезен:
 * SQLite в backend всё равно сериализует под write-lock'ом, а у клиента
 * сетевые сбои чаще «всё или ничего».
 */

import { ApiError, apiFetch, type RequestOptions } from "@/api/client";
import { getMe } from "@/api/auth";
import type { QueryClient } from "@tanstack/react-query";

const VERSION = 1;
const KEY_PREFIX = "field.queue.";

export type QueueKind = "entry-create" | "entry-update" | "entry-delete" | "sign";

export interface QueuedRequest {
  /** Локальный uuid для дедупа и удаления из очереди. */
  id: string;
  kind: QueueKind;
  method: "POST" | "PUT" | "DELETE";
  /** Относительный path для `apiFetch` (без `/api`). */
  path: string;
  /** JSON body — undefined для DELETE. */
  body?: unknown;
  /**
   * `work_date` (YYYY-MM-DD) — нужен для `invalidateQueries(['timesheet', date])`
   * после успешного drain'а.
   */
  date: string;
  enqueuedAt: number;
}

interface PersistedShape {
  v: number;
  items: QueuedRequest[];
}

function storageKey(userId: number): string {
  return `${KEY_PREFIX}${userId}`;
}

function readStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function loadQueue(userId: number): QueuedRequest[] {
  const ls = readStorage();
  if (!ls) return [];
  const raw = ls.getItem(storageKey(userId));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as PersistedShape;
    if (!parsed || parsed.v !== VERSION || !Array.isArray(parsed.items)) return [];
    return parsed.items;
  } catch {
    return [];
  }
}

function saveQueue(userId: number, items: QueuedRequest[]): void {
  const ls = readStorage();
  if (!ls) return;
  try {
    if (items.length === 0) ls.removeItem(storageKey(userId));
    else ls.setItem(storageKey(userId), JSON.stringify({ v: VERSION, items }));
  } catch {
    // QuotaExceeded или приватный режим — глотаем; в худшем случае при reload
    // мутация просто отправится заново вручную пользователем.
  }
}

export function enqueue(
  userId: number,
  req: Omit<QueuedRequest, "id" | "enqueuedAt">,
): QueuedRequest {
  const items = loadQueue(userId);
  const queued: QueuedRequest = {
    ...req,
    id: makeId(),
    enqueuedAt: Date.now(),
  };
  items.push(queued);
  saveQueue(userId, items);
  return queued;
}

function removeById(userId: number, id: string): void {
  const items = loadQueue(userId).filter((q) => q.id !== id);
  saveQueue(userId, items);
}

export function clearQueue(userId: number): void {
  const ls = readStorage();
  if (!ls) return;
  try {
    ls.removeItem(storageKey(userId));
  } catch {
    /* ignore */
  }
}

export function queueLength(userId: number): number {
  return loadQueue(userId).length;
}

export function hasPendingEntries(userId: number): boolean {
  return loadQueue(userId).some((q) => q.kind !== "sign");
}

// ---------------------------------------------------------------------------
// Network-error classification + replay
// ---------------------------------------------------------------------------

/**
 * Сетевая ошибка fetch'а — нет ответа от сервера (DNS, TCP, offline, CORS-fail).
 * `ApiError` строится только когда HTTP-ответ был — значит, по определению,
 * НЕ сетевая ошибка.
 *
 * NB: `navigator.onLine === false` — слабый сигнал (lies на корпоративных
 * captive-portal'ах), поэтому опираемся на сам факт TypeError из fetch.
 */
export function isNetworkError(err: unknown): boolean {
  if (err instanceof ApiError) return false;
  if (err instanceof TypeError) return true;
  if (err instanceof Error && /network|failed to fetch|load failed/i.test(err.message)) {
    return true;
  }
  return false;
}

function isCsrfFailure(err: ApiError): boolean {
  if (err.status !== 403) return false;
  const detail = err.detail;
  if (typeof detail === "string") return /csrf/i.test(detail);
  if (detail && typeof detail === "object" && "code" in detail) {
    const code = (detail as { code: unknown }).code;
    return typeof code === "string" && code.startsWith("csrf");
  }
  return false;
}

async function executeQueued(req: QueuedRequest): Promise<void> {
  const opts: RequestOptions = {
    method: req.method,
  };
  if (req.body !== undefined) opts.body = req.body;
  await apiFetch(req.path, opts);
}

interface DrainState {
  running: boolean;
  /** Сериализуем drain'ы — два onlines подряд не должны бить очередь параллельно. */
  next: (() => Promise<void>) | null;
}

const drainState: DrainState = { running: false, next: null };

/**
 * Слить очередь до конца, в порядке enqueue. Останавливается:
 *   - successfully (queue empty) → resolve true
 *   - на network-error любой записи → resolve false (записи остаются)
 *   - на терминальной (4xx кроме 403-csrf) → запись удаляется, очередь идёт
 *     дальше; в конце runner notify'ит callback'ом с количеством дропов.
 *
 * `onDropped(req, err)` — callback для toast'а из вызывающей стороны (i18n
 * живёт в реактах, очередь — pure). Вызывается ПОСЛЕ удаления.
 */
export async function drainQueue(
  userId: number,
  queryClient: QueryClient,
  callbacks: {
    onDropped?: (req: QueuedRequest, err: ApiError) => void;
    onProgress?: (remaining: number) => void;
  } = {},
): Promise<boolean> {
  // Сериализация: если drain уже идёт, ставим «один-в-очереди» follow-up.
  if (drainState.running) {
    drainState.next = () => drainQueue(userId, queryClient, callbacks).then(() => undefined);
    return false;
  }
  drainState.running = true;
  try {
    let allOk = true;
    // Каждую итерацию перечитываем — снаружи могут добавлять.
    // Не более 200 шагов на один проход (защита от бесконечного цикла, не
    // должно случаться в реальности).
    for (let safety = 0; safety < 200; safety += 1) {
      const items = loadQueue(userId);
      if (items.length === 0) break;
      const head = items[0]!;
      try {
        await executeQueued(head);
        removeById(userId, head.id);
        await queryClient.invalidateQueries({ queryKey: ["timesheet", head.date] });
        callbacks.onProgress?.(loadQueue(userId).length);
      } catch (err) {
        if (isNetworkError(err)) {
          allOk = false;
          break;
        }
        if (err instanceof ApiError && isCsrfFailure(err)) {
          // Принудительно рефрешим csrf cookie и повторяем 1 раз.
          try {
            await getMe();
            await executeQueued(head);
            removeById(userId, head.id);
            await queryClient.invalidateQueries({ queryKey: ["timesheet", head.date] });
            continue;
          } catch (retryErr) {
            if (isNetworkError(retryErr)) {
              allOk = false;
              break;
            }
            if (retryErr instanceof ApiError) {
              removeById(userId, head.id);
              callbacks.onDropped?.(head, retryErr);
              continue;
            }
            // Неизвестный non-API non-network — дроп, чтоб не клинить очередь.
            removeById(userId, head.id);
            allOk = false;
            continue;
          }
        }
        if (err instanceof ApiError) {
          removeById(userId, head.id);
          callbacks.onDropped?.(head, err);
          continue;
        }
        // Полностью неизвестная ошибка — дропаем, чтоб не зависнуть навеки.
        removeById(userId, head.id);
        allOk = false;
      }
    }
    return allOk;
  } finally {
    drainState.running = false;
    const follow = drainState.next;
    drainState.next = null;
    if (follow) void follow();
  }
}

// ---------------------------------------------------------------------------
// Helpers — fluent enqueue API used by hooks
// ---------------------------------------------------------------------------

function makeId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
